import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ActiveFiltersProvider } from "./context/ActiveFiltersContext";
import { Toaster } from "sonner";
import { Suspense, lazy } from "react";

// Layout - keep in main bundle
import MainLayout from "./components/layout/MainLayout";

// Critical pages - keep in main bundle for fast initial load
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Landing from "./pages/Landing";

// Lazy loaded pages - code split for better performance
const Sites = lazy(() => import("./pages/Sites"));
const SiteForm = lazy(() => import("./pages/SiteForm"));
const SiteDetail = lazy(() => import("./pages/SiteDetail"));
const Assets = lazy(() => import("./pages/Assets"));
const AssetForm = lazy(() => import("./pages/AssetForm"));
const AssetDetail = lazy(() => import("./pages/AssetDetail"));
const WorkOrders = lazy(() => import("./pages/WorkOrders"));
const WorkOrderForm = lazy(() => import("./pages/WorkOrderForm"));
const WorkOrderDetail = lazy(() => import("./pages/WorkOrderDetail"));
const Inspections = lazy(() => import("./pages/Inspections"));
const InspectionForm = lazy(() => import("./pages/InspectionForm"));
const InspectionDetail = lazy(() => import("./pages/InspectionDetail"));
const Checklists = lazy(() => import("./pages/Checklists"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const MaintenanceCalendar = lazy(() => import("./pages/MaintenanceCalendar"));
const EmailReminders = lazy(() => import("./pages/EmailReminders"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportBuilder = lazy(() => import("./pages/ReportBuilder"));
const Users = lazy(() => import("./pages/Users"));
const Settings = lazy(() => import("./pages/Settings"));
const ContractorPortal = lazy(() => import("./pages/ContractorPortal"));
const MFASettings = lazy(() => import("./pages/MFASettings"));
const AdvancedAnalytics = lazy(() => import("./pages/AdvancedAnalytics"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const ConflictResolution = lazy(() => import("./pages/ConflictResolution"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));

// New modules
const Vendors = lazy(() => import("./pages/Vendors"));
const VendorDetail = lazy(() => import("./pages/VendorDetail"));
const QuickReport = lazy(() => import("./pages/QuickReport"));
const Leads = lazy(() => import("./pages/Leads"));
const Budget = lazy(() => import("./pages/Budget"));
const Sustainability = lazy(() => import("./pages/Sustainability"));
const FloorPlans = lazy(() => import("./pages/FloorPlans"));
const PPMSchedules = lazy(() => import("./pages/PPMSchedules"));
const Operations = lazy(() => import("./pages/Operations"));
const SavedViewTemplates = lazy(() => import("./pages/SavedViewTemplates"));
const ViewSlugRedirect = lazy(() => import("./pages/ViewSlugRedirect"));
const Scan = lazy(() => import("./pages/Scan"));

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff6600]"></div>
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff6600]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff6600]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Wrapper for lazy loaded components
const LazyPage = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Contractor Portal - No auth required */}
      <Route path="/contractor/:token" element={<LazyPage><ContractorPortal /></LazyPage>} />

      {/* Auth Callback for SSO */}
      <Route path="/auth/callback" element={<LazyPage><AuthCallback /></LazyPage>} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Sites */}
        <Route path="/sites" element={<LazyPage><Sites /></LazyPage>} />
        <Route path="/sites/new" element={<LazyPage><SiteForm /></LazyPage>} />
        <Route path="/sites/:id" element={<LazyPage><SiteDetail /></LazyPage>} />
        <Route path="/sites/:id/edit" element={<LazyPage><SiteForm /></LazyPage>} />
        
        {/* Assets */}
        <Route path="/assets" element={<LazyPage><Assets /></LazyPage>} />
        <Route path="/assets/new" element={<LazyPage><AssetForm /></LazyPage>} />
        <Route path="/assets/:id" element={<LazyPage><AssetDetail /></LazyPage>} />
        <Route path="/assets/:id/edit" element={<LazyPage><AssetForm /></LazyPage>} />
        
        {/* Work Orders */}
        <Route path="/work-orders" element={<LazyPage><WorkOrders /></LazyPage>} />
        <Route path="/work-orders/new" element={<LazyPage><WorkOrderForm /></LazyPage>} />
        <Route path="/work-orders/:id" element={<LazyPage><WorkOrderDetail /></LazyPage>} />
        <Route path="/work-orders/:id/edit" element={<LazyPage><WorkOrderForm /></LazyPage>} />
        
        {/* Inspections */}
        <Route path="/inspections" element={<LazyPage><Inspections /></LazyPage>} />
        <Route path="/inspections/new" element={<LazyPage><InspectionForm /></LazyPage>} />
        <Route path="/inspections/:id" element={<LazyPage><InspectionDetail /></LazyPage>} />
        <Route path="/inspections/:id/edit" element={<LazyPage><InspectionForm /></LazyPage>} />
        
        {/* Checklists */}
        <Route path="/checklists" element={<LazyPage><Checklists /></LazyPage>} />
        
        {/* Calendar */}
        <Route path="/calendar" element={<LazyPage><CalendarPage /></LazyPage>} />
        
        {/* Maintenance Calendar */}
        <Route path="/maintenance" element={<LazyPage><MaintenanceCalendar /></LazyPage>} />
        
        {/* Vendors */}
        <Route path="/vendors" element={<LazyPage><Vendors /></LazyPage>} />
        <Route path="/vendors/:vendorId" element={<LazyPage><VendorDetail /></LazyPage>} />
        <Route path="/report" element={<LazyPage><QuickReport /></LazyPage>} />
        <Route path="/admin/leads" element={<LazyPage><Leads /></LazyPage>} />
        
        {/* Budget */}
        <Route path="/budget" element={<LazyPage><Budget /></LazyPage>} />
        
        {/* Sustainability */}
        <Route path="/sustainability" element={<LazyPage><Sustainability /></LazyPage>} />
        
        {/* Space Visualisation - Floor Plans */}
        <Route path="/floor-plans" element={<LazyPage><FloorPlans /></LazyPage>} />
        <Route path="/space-visualisation" element={<LazyPage><FloorPlans /></LazyPage>} />

        {/* Preventive Maintenance */}
        <Route path="/ppm" element={<LazyPage><PPMSchedules /></LazyPage>} />

        {/* Operations: SLA / Audit / Bulk Data */}
        <Route path="/operations" element={<LazyPage><Operations /></LazyPage>} />

        {/* Mobile QR scan deep-link */}
        <Route path="/scan/:assetId" element={<LazyPage><Scan /></LazyPage>} />
        
        {/* Email Reminders */}
        <Route path="/email-reminders" element={<LazyPage><EmailReminders /></LazyPage>} />
        
        {/* Reports */}
        <Route path="/reports" element={<LazyPage><Reports /></LazyPage>} />
        
        {/* Users */}
        <Route path="/users" element={<LazyPage><Users /></LazyPage>} />
        
        {/* Settings */}
        <Route path="/settings" element={<LazyPage><Settings /></LazyPage>} />
        
        {/* Security */}
        <Route path="/security/mfa" element={<LazyPage><MFASettings /></LazyPage>} />
        
        {/* Advanced Analytics */}
        <Route path="/analytics" element={<LazyPage><AdvancedAnalytics /></LazyPage>} />
        
        {/* Report Builder */}
        <Route path="/report-builder" element={<LazyPage><ReportBuilder /></LazyPage>} />
        
        {/* Admin Settings */}
        <Route path="/admin/settings" element={<LazyPage><AdminSettings /></LazyPage>} />
        
        {/* Audit Logs - Admin Only */}
        <Route path="/admin/audit-logs" element={<LazyPage><AuditLogs /></LazyPage>} />

        {/* Saved-view Templates - Admin Only */}
        <Route path="/admin/saved-view-templates" element={<LazyPage><SavedViewTemplates /></LazyPage>} />
        
        {/* Slug permalink resolver: /v/:slug → applies the named saved view */}
        <Route path="/v/:slug" element={<LazyPage><ViewSlugRedirect /></LazyPage>} />
        
        {/* Offline Sync & Conflict Resolution */}
        <Route path="/sync/conflicts" element={<LazyPage><ConflictResolution /></LazyPage>} />
      </Route>

      {/* Default redirect — Landing for unauthenticated, dashboard for authenticated */}
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ActiveFiltersProvider>
          <AppRoutes />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                fontFamily: "Inter, sans-serif",
              },
            }}
          />
        </ActiveFiltersProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
